import { useApiStore } from '@/stores/apiStore';
import { OrgUserApi } from '@/../generated-client/apis/OrgUserApi';
import {
  MergedUserAdminDelete,
  MergedUserDelete,
  MergedUserSelfUpdateSettings,
  OrgUserAdminUpdate,
  OrgUserSelfCreateRequest,
  OrgUserSelfUpdate,
  OrgUserUpdateGroup,
  OrgUserUpdateOrganization,
  UserHistoryObservedEmergencyModel as ApiObserved,
  OrgUserUpdateUnifiedConfigRequest,
  OrgUserUpdatePvdConfigRequest,
  OrgUserUpdateEmergencyConfigRequest,
} from '../../generated-client/models';
import { UserHistoryObservedEmergencyModel as DomainObserved } from '@/models/Emergency/UserHistoryObservedEmergencyModel';

let orgUserApi: OrgUserApi | null = null;
let lastConfig: unknown = null;

function getOrgUserApi(): OrgUserApi {
  const { apiConfig } = useApiStore.getState();

  // if config changed (token refresh), rebuild
  if (orgUserApi === null || lastConfig !== apiConfig) {
    orgUserApi = new OrgUserApi(apiConfig);
    lastConfig = apiConfig;
  }

  return orgUserApi;
}

export const getSelf = async () => {
  const api = getOrgUserApi();

  return await api.apiOrgUserGetSelfV1Get();
};

/**
 *
 * @param m the map of observedEmergencies
 * @returns UserHistoryObservedEmergencyModel defined by us. We want to do this because there are a few different
 * fields, and our version has some helper methods that don't exist on the API response object
 */
export function hydrateObservedMap(
  m: Record<string, ApiObserved> | null | undefined,
): Record<string, DomainObserved> | null {
  if (!m) return null;
  const out: Record<string, DomainObserved> = {};
  for (const [k, v] of Object.entries(m)) {
    out[k] = new DomainObserved(v); // ctor takes the same fields
  }
  return out;
}

export const addUser = async (
  orgCode: string,
  groupName: string,
  firstName: string,
  lastName: string,
  phoneNumber: string,
  orgPin: string,
) => {
  const jsonObject: OrgUserSelfCreateRequest = {
    org_code: orgCode,
    group_name: groupName,
    first_name: firstName,
    last_name: lastName,
    phone_number: phoneNumber,
    join_org_pin: orgPin,
  };
  const api = getOrgUserApi();

  return await api.apiOrgUserAddPost({
    orgUserSelfCreateRequest: jsonObject,
  });
};

export const deleteUser = async (
  orgCode: string,
  orgUserAzureId: string,
  UserHistoryAzureId: string,
  userPrivateSettingsAzureId: string,
  organizationAzureId: string,
) => {
  const jsonObject: MergedUserDelete = {
    org_code: orgCode,
    org_user_azure_id: orgUserAzureId,
    user_history_azure_id: UserHistoryAzureId,
    user_private_settings_azure_id: userPrivateSettingsAzureId,
    organization_azure_id: organizationAzureId,
  };
  const api = getOrgUserApi();

  return await api.apiOrgUserDeleteDelete({
    mergedUserDelete: jsonObject,
  });
};

export const deleteUserViaAdmin = async (
  orgCode: string,
  orgUserAzureId: string,
  organizationAzureId: string,
) => {
  const jsonObject: MergedUserAdminDelete = {
    org_code: orgCode,
    org_user_azure_id: orgUserAzureId,
    organization_azure_id: organizationAzureId,
  };
  const api = getOrgUserApi();

  return await api.apiOrgUserDeleteViaAdminDelete({
    mergedUserAdminDelete: jsonObject,
  });
};

export const getIfIExist = async () => {
  const api = getOrgUserApi();

  return await api.apiOrgUserGetIfIExistGet();
};

export const updateProfile = async (profile: OrgUserSelfUpdate) => {
  const api = getOrgUserApi();

  return api.apiOrgUserUpdateViaSelfPut({ orgUserSelfUpdate: profile });
};

export const updateProfileSettings = async (
  profile: MergedUserSelfUpdateSettings,
) => {
  const api = getOrgUserApi();

  return await api.apiOrgUserUpdateSettingsViaSelfPut({
    mergedUserSelfUpdateSettings: profile,
  });
};

export const updateUserViaAdmin = async (userViaAdmin: OrgUserAdminUpdate) => {
  const api = getOrgUserApi();

  return await api.apiOrgUserUpdateViaAdminPut({
    orgUserAdminUpdate: userViaAdmin,
  });
};

export const updateUnifiedConfigViaAdmin = async (
  payload: OrgUserUpdateUnifiedConfigRequest,
) => {
  const api = getOrgUserApi();

  return await api.apiOrgUserUpdateUnifiedConfigPut({
    orgUserUpdateUnifiedConfigRequest: payload,
  });
};

export const updateOrganization = async (
  orgData: OrgUserUpdateOrganization,
) => {
  const api = getOrgUserApi();

  return await api.apiOrgUserUpdateOrganizationPut({
    orgUserUpdateOrganization: orgData,
  });
};

export const updateGroup = async (groupData: OrgUserUpdateGroup) => {
  const api = getOrgUserApi();

  return await api.apiOrgUserUpdateGroupPut({
    orgUserUpdateGroup: groupData,
  });
};

export const updatePvdConfigViaAdmin = async (
  payload: OrgUserUpdatePvdConfigRequest,
) => {
  const api = getOrgUserApi();
  return await api.apiOrgUserUpdatePvdConfigPut({ orgUserUpdatePvdConfigRequest: payload });
};

export const updateEmergencyConfigViaAdmin = async (
  payload: OrgUserUpdateEmergencyConfigRequest,
) => {
  const api = getOrgUserApi();
  return await api.apiOrgUserUpdateEmergencyConfigPut({ orgUserUpdateEmergencyConfigRequest: payload });
};
